import { overviewKey } from '../overviewCard';
import type { ThresholdSegment } from '../overviewCard';

/** Testid prefix, icons and copy keys for the break-even card. */
export const BREAK_EVEN = {
    slot: 'break-even',
    icon: 'solar:target-bold-duotone',
    ctaIcon: 'solar:add-circle-bold-duotone',
    keys: {
        title: overviewKey('breakEven', 'title'),
        question: overviewKey('breakEven', 'question'),
        revenue: overviewKey('breakEven', 'revenue'),
        reached: overviewKey('breakEven', 'reached'),
        notReached: overviewKey('breakEven', 'notReached'),
        profitZone: overviewKey('breakEven', 'profitZone'),
        firstDay: overviewKey('breakEven', 'firstDay'),
        lastDay: overviewKey('breakEven', 'lastDay'),
        profitable: overviewKey('breakEven', 'profitable'),
        unprofitable: overviewKey('breakEven', 'unprofitable'),
        deltaEarlier: overviewKey('breakEven', 'deltaEarlier'),
        deltaLater: overviewKey('breakEven', 'deltaLater'),
        scaleAria: overviewKey('breakEven', 'scaleAria'),
        emptyBody: overviewKey('breakEven', 'emptyBody'),
        emptyCta: overviewKey('breakEven', 'emptyCta'),
    },
};

/**
 * The month as a bar: the stretch spent covering costs, then the stretch spent
 * earning. A month that never broke even is red end to end — there was no
 * profit stretch.
 */
export const breakEvenSegments = (
    breakEvenDay: number | null,
    daysInMonth: number,
): ThresholdSegment[] => {
    if (breakEvenDay === null) {
        return [{ tone: 'danger', ratio: 1 }];
    }
    const covered = breakEvenDay / daysInMonth;
    return [
        { tone: 'danger', ratio: covered },
        { tone: 'success', ratio: 1 - covered },
    ];
};

/** A copy key plus the values it interpolates — resolved here, rendered by the View. */
export type BreakEvenLabel = { key: string; values?: Record<string, number> };

/**
 * How the month's break-even day is worded. Three cases, decided here rather
 * than in the View: which sentence is true is a fact about the business, and
 * `.tsx` files do not decide those.
 */
export const resolveBreakEvenLabel = (
    breakEvenDay: number | null,
): BreakEvenLabel =>
    breakEvenDay === null
        ? { key: BREAK_EVEN.keys.notReached }
        : { key: BREAK_EVEN.keys.reached, values: { day: breakEvenDay } };

/**
 * How the movement against last month is worded, or null when there is no month
 * to compare with. `count` is always positive — the direction is carried by the
 * key, because "3 days later" and "3 days earlier" are opposite facts, not one
 * fact with a sign.
 */
export const resolveBreakEvenDelta = (
    deltaDays: number | null,
): BreakEvenLabel | null => {
    if (deltaDays === null) {
        return null;
    }
    return deltaDays >= 0
        ? { key: BREAK_EVEN.keys.deltaEarlier, values: { count: deltaDays } }
        : { key: BREAK_EVEN.keys.deltaLater, values: { count: -deltaDays } };
};
